import type { OnInit } from '@angular/core';
import { ChangeDetectorRef, Component, HostListener, inject, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { BehaviorSubject, Subject, take } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';

import { ELanguages } from '@common/enums';

import { type ImageResult } from './types';
import { ImageSearchComponent } from './components/image-search/image-search.component';
import { ImagePreviewComponent } from './components/image-preview/image-preview.component';
import { UnsplashProvider } from './providers/unsplash';
import * as langs from '../assets/translations';
import { IMAGE_UTMS } from '@sites/images/src/app/constants';

@Component({
    imports: [CommonModule, ImageSearchComponent, ImagePreviewComponent, TranslateModule],
    selector: 'sp-unsplash-modal',
    templateUrl: './unsplash.component.html',
    styles: [
        `
            .modal-body {
                max-height: 70vh;
                overflow-y: auto;
                padding-top: 0;
            }
            .image-uploading-state {
                background-color: #f5f5f5;
                display: flex;
                gap: 15px;
                width: 100%;
                min-height: 50vh;
                justify-content: center;
                align-items: center;
                flex-direction: column;
            }
        `,
    ],
})
export class UnsplashComponent implements OnInit {
    @Input() public apiUrl!: string;
    @Input() public lang!: ELanguages;

    private readonly bsModalRef: BsModalRef = inject(BsModalRef);
    private readonly http: HttpClient = inject(HttpClient);
    private readonly translate: TranslateService = inject(TranslateService);
    private readonly cookieService: CookieService = inject(CookieService);
    private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

    public readonly imageUploading$ = new BehaviorSubject<boolean>(false);

    private imageProvider!: UnsplashProvider;

    public onConfirm: Subject<File> = new Subject<File>();
    public selectedImage: ImageResult | null = null;

    public IMAGE_UTMS = IMAGE_UTMS;

    @HostListener('document:keydown.escape')
    public discardPreview() {
        this.imageUploading$.pipe(take(1)).subscribe((uploading) => {
            if (uploading) return;
            this.selectImage(null);
        });
    }

    ngOnInit(): void {
        this.imageProvider = new UnsplashProvider({ apiUrl: this.apiUrl });
        const shouldMerge = true;

        this.translate.setTranslation(
            this.getCurrentLang,
            langs[this.getCurrentLang as keyof typeof langs],
            shouldMerge
        );
        this.translate.use(this.getCurrentLang);
        this.changeDetectorRef.markForCheck();
    }

    public selectImage(image: ImageResult | null) {
        this.selectedImage = image;
    }

    public confirmImage() {
        if (!this.selectedImage) return;

        this.imageUploading$.next(true);
        this.http
            .get(this.selectedImage.urls.custom_large, { observe: 'response', responseType: 'blob' })
            .pipe(take(1))
            .subscribe((response) => {
                if (!this.selectedImage) return;
                const blob = response.body!;
                const { authorName, id, extension } = this.selectedImage;
                let sanitizedName = authorName;

                if (authorName) {
                    sanitizedName = authorName.replace(/[^\p{L}\p{N}\s\-_]/gu, '_').replace(/^_+|_+$/g, '');
                }

                const file = new File([blob], `${sanitizedName}_${Date.now()}_unsplash_id_${id}.${extension}`, {
                    type: 'image/jpeg',
                });
                this.onConfirm.next(file);
                this.onConfirm.complete();
                this.imageUploading$.complete();
                this.imageProvider.trackDownload(this.selectedImage!);
            });
    }

    public onClose() {
        this.imageUploading$.pipe(take(1)).subscribe((isUploading) => {
            if (isUploading) return;
            this.bsModalRef.hide();
            this.imageUploading$.complete();
        });
    }

    public get getCurrentLang(): ELanguages {
        const langCookie: string = this.cookieService.get('lang').replace('-', '_');
        return this.lang || ((langCookie ? langCookie.split(':')[0] : ELanguages.EN) as ELanguages);
    }
}
